import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangleIcon, CaretDownIcon, DeleteIcon, ClockIcon, ChevronRightIcon, CheckCircleIcon } from '@shopify/polaris-icons';
import { groupedTools } from './tools';
import { useAuth } from './AuthComponent';
import { useAlerts, navBadges } from './alerts';
import { Ic, StatusPill } from './ui';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function formatWhen(value) {
  if (!value) return '';
  const d = new Date(String(value).endsWith('Z') || String(value).includes('+') ? value : `${value}Z`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Which tool groups this person has folded up, kept in this browser under
// their username so a shared machine doesn't hand one person's layout to the next.
const foldKey = (user) => `dashboard-folded:${user || ''}`;
const readFolded = (user) => {
  try { return new Set(JSON.parse(localStorage.getItem(foldKey(user)) || '[]')); } catch { return new Set(); }
};
const writeFolded = (user, folded) => {
  try { localStorage.setItem(foldKey(user), JSON.stringify([...folded])); } catch { /* then it just isn't remembered */ }
};

// Everything the signed-in user can act on, most urgent first.
function attentionItems(alerts) {
  if (!alerts) return [];
  const items = [];
  for (const job of alerts.cronFailing || []) {
    items.push({
      key: `cron-${job.job_name}`, tone: 'crit', icon: AlertTriangleIcon, path: '/cron-monitor',
      title: `${job.job_name} failed`,
      sub: `Cron monitor${job.exit_code != null ? ` · exit code ${job.exit_code}` : ''}${job.started_at ? ` · ${formatWhen(job.started_at)}` : ''}`,
    });
  }
  const ob = alerts.offboarding;
  for (const run of ob?.attention || []) {
    const failed = run.status === 'failure';
    items.push({
      key: `ob-${run.id}`, tone: failed ? 'crit' : 'hold', icon: AlertTriangleIcon,
      path: typeof run.id === 'number' ? `/offboarding/runs/${run.id}` : '/offboarding?tab=history',
      title: `${run.full_name || run.email}: ${failed ? 'a step failed' : 'needs a decision'}`,
      sub: 'Offboarding',
    });
  }
  if (ob?.deletion?.length) {
    const n = ob.deletion.length;
    items.push({
      key: 'ob-delete', tone: 'unf', icon: DeleteIcon, path: '/offboarding?tab=delete',
      title: `${plural(n, 'Workspace account', 'Workspace accounts')} waiting for final deletion`,
      sub: 'Offboarding · archive finished, Drive transfer and delete remain',
    });
  }
  if (alerts.teamaticalAudit?.length) {
    const n = alerts.teamaticalAudit.length;
    items.push({
      key: 'tp-audit', tone: 'hold', icon: AlertTriangleIcon, path: '/teamatical-prep?tab=audit',
      title: `${plural(n, 'Teamatical product', 'Teamatical products')} need${n === 1 ? 's' : ''} attention`,
      sub: 'Teamatical prep · audit found drift or duplicate IDs',
    });
  }
  for (const run of alerts.teamatical || []) {
    const failed = run.status === 'failed';
    const kept = (run.kept_locations || []).map((k) => k.location).join(', ');
    items.push({
      key: `tp-${run.id}`, tone: failed ? 'crit' : 'hold', icon: AlertTriangleIcon, path: '/teamatical-prep?tab=history',
      title: `${run.title}: ${failed ? 'a step failed' : `still stocked at ${kept}`}`,
      sub: failed ? 'Teamatical prep · retry from History' : 'Teamatical prep · open orders; retry cleanup once they ship',
    });
  }
  if (alerts.poSession && !alerts.poSession.ok) {
    items.push({
      key: 'po', tone: 'unf', icon: ClockIcon, path: '/purchase-orders',
      title: 'Shopify admin session expired',
      sub: 'Purchase orders · upload a fresh HAR file',
    });
  }
  return items;
}

function Dashboard() {
  const navigate = useNavigate();
  const { permissions, user } = useAuth();
  const { alerts } = useAlerts();
  const groups = groupedTools(permissions);
  const badges = navBadges(alerts);
  const items = attentionItems(alerts);

  const [folded, setFolded] = useState(() => readFolded(user));
  useEffect(() => { setFolded(readFolded(user)); }, [user]);
  const toggleFold = (id) => {
    const next = new Set(folded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setFolded(next);
    writeFolded(user, next);
  };

  return (
    <div className="uh-page">
      <div className="uh-ph">
        <div>
          <h1 className="uh-h1">Dashboard</h1>
          <div className="uh-sub">What needs you across every tool, then the tools themselves.</div>
        </div>
      </div>
      <div className="uh-dash">
        <div className="uh-col">
          {groups.map((g) => (
            <div className="uh-tgroup" key={g.id}>
              <h3>
                <button type="button" className="uh-tg-h" aria-expanded={!folded.has(g.id)} aria-controls={`tg-${g.id}`} onClick={() => toggleFold(g.id)}>
                  <Ic source={CaretDownIcon} sm />
                  {g.title}
                  {folded.has(g.id) && <span className="n">{plural(g.tools.length, 'tool', 'tools')}</span>}
                </button>
              </h3>
              <div className={`uh-tfold${folded.has(g.id) ? ' in' : ''}`} id={`tg-${g.id}`}>
              <div className="uh-tools">
                {g.tools.map((t) => {
                  const b = badges[t.id];
                  return (
                    <button type="button" className="uh-tool" key={t.id} onClick={() => navigate(t.path)}>
                      <span className="ti"><Ic source={t.icon} /></span>
                      <span>
                        <span className="tn">
                          {t.title}
                          {t.stub && <StatusPill tone="neu">Coming soon</StatusPill>}
                          {b && <StatusPill tone={b.tone === 'crit' ? 'can' : 'hold'}>{b.label}</StatusPill>}
                        </span>
                        <span className="td">{t.description}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              </div>
            </div>
          ))}
        </div>

        <div className="uh-card">
          <div className="uh-card-h">
            <h2 className="uh-h2">Needs attention</h2>
            <span className="uh-sp" />
            {alerts && <span className="uh-tcount">{items.length}</span>}
          </div>
          {!alerts ? (
            <div className="uh-empty">Checking…</div>
          ) : items.length === 0 ? (
            <div className="uh-empty"><Ic source={CheckCircleIcon} className="uh-faint" /> Nothing needs you right now.</div>
          ) : (
            <ul className="uh-attn">
              {items.map((it) => (
                <li key={it.key}>
                  <button type="button" onClick={() => navigate(it.path)}>
                    <span className={`ai ${it.tone}`}><Ic source={it.icon} /></span>
                    <span>
                      <span className="at">{it.title}</span>
                      <span className="as">{it.sub}</span>
                    </span>
                    <Ic source={ChevronRightIcon} sm className="uh-faint" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
